import { Button, LoadingOverlay, Modal, TextInput } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { IconCheck, IconEdit, IconEditCircle, IconX } from "@tabler/icons-react"
import { motion } from "framer-motion"
import {
  AlertTriangle,
  DollarSign,
  Package,
  PauseCircle,
  TrendingUp,
} from "lucide-react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useParams } from "react-router-dom"
import {
  CollectionQuery,
  Filter,
  Order,
} from "../../../models/collection.model"
import {
  EntityConfig,
  entityViewMode,
} from "../../../models/entity-config.model"
import { Payment, PaymentStatus } from "../../../models/payment.model"
import Confirmation from "../../../shared/component/confirmation/action-confirmation"
import EntityList from "../../../shared/component/entity-list/entity-list.component"
import FileViewerModal from "../../../shared/component/file-viewer/file-viewer-modal.component"
import { useCurrentUser } from "../../../shared/hooks/current-user.hook"
import { dateFormat } from "../../../shared/utility/date-format/date-format"
import { formatCurrency } from "../../dashboard/component/ReportCard"
import StatCard from "../../dashboard/component/stat-cart"
import TotalCollectedPaymentStatCard from "../../dashboard/component/total-collected-payment-stat-cart"
import TotalPaymentStatCard from "../../dashboard/component/total-payment-stat-cart"
import { exportTenants } from "../api/payment.api"
import ExportPaymentModalComponent from "../component/export-payment-modal.component"
import {
  useChangePaymentStatusMutation,
  useLazyCountPaymentsQuery,
  useLazyGetPaymentsQuery,
  useUpdateFsNumberMutation,
} from "../store/payment.query"

export default function PaymentPage() {
  const params = useParams()
  const location = useLocation()
  const { t } = useTranslation()
  const [receipt, setReceipt] = useState<File | null>(null)
  const [viewMode, setViewMode] = useState<entityViewMode>("list")
  const [openedExport, { open: openExport, close: closeExport }] =
    useDisclosure()
  const [openedImport, { open: openImport, close: closeImport }] =
    useDisclosure()

  const [openedFs, { open: openFs, close: closeFs }] = useDisclosure()

  const [fileOpened, { open: fileOpen, close: fileClose }] = useDisclosure()
  const [selectedFile, setSelectedFile] = useState<string[]>([])

  const [selectedFsNumber, setSelectedFsNumber] = useState<
    string | undefined | null
  >(null)
  const [selectedPayment, setSelectedPayment] = useState<string | null>(null)
  // const [selectedFileName]
  const [collection, setCollection] = useState<CollectionQuery>({
    skip: 0,
    top: 10,
    includes: [
      "tenant",
      "lease",
      // "lease.unit",
      // "unit.property",
      // "property.owner",
      "owner",
    ],
    orderBy: [{ field: "createdAt", direction: "desc" }],
  })

  const [getPayments, payments] = useLazyGetPaymentsQuery()
  const [countPayments, totalPayments] = useLazyCountPaymentsQuery()
  const [countPendingPayments, pendingPayments] = useLazyCountPaymentsQuery()
  const [countSuccessPayments, successPayments] = useLazyCountPaymentsQuery()
  const [countRejectedPayments, rejectedPayments] = useLazyCountPaymentsQuery()
  const [userInfo, userId, ownerInfo] = useCurrentUser()
  const [changeStatus, changeStatusResponse] = useChangePaymentStatusMutation()
  const [updateFsNumber, updateResponse] = useUpdateFsNumberMutation()

  const generateFilterOption = (status?: PaymentStatus): Filter[][] => {
    if (userInfo.role.key == "super_admin" && status) {
      // return [[{ field: "enabled", value: value as boolean, operator: "=" }]]
      return [[{ field: "paymentStatus", operator: "=", value: status }]]
    }

    if (userInfo.role.key == "tenant" && status) {
      return [
        [{ field: "tenantId", value: userInfo.id, operator: "=" }],
        [{ field: "paymentStatus", operator: "=", value: status }],
      ]
    }

    if (status) {
      return [
        // [{ field: "enabled", value: value as boolean, operator: "=" }],
        [{ field: "ownerId", value: ownerInfo?.parentOwnerId, operator: "=" }],
        [{ field: "paymentStatus", operator: "=", value: status }],
        [{ field: "leaseId", operator: "notNull" }],
      ]
    }

    return []
  }
  useEffect(() => {
    if (userInfo?.role?.key == "super_admin") {
      getPayments({
        ...collection,
        // filter: [
        //   [{ field: "owner.confidential", value: false, operator: "=" }],
        // ],
      })
    } else if (userInfo?.role?.key == "tenant") {
      getPayments({
        ...collection,
        filter: [
          [
            {
              field: "tenant",
              value: userInfo?.id,
              operator: "=",
            },
          ],
        ],
      })
    } else {
      getPayments({
        ...collection,
        filter: [
          [
            {
              field: "ownerId",
              value: ownerInfo?.parentOwnerId,
              operator: "=",
            },
          ],
          [
            {
              field: "leaseId",
              operator: "notNull",
            },
          ],
        ],
      })
    }
  }, [collection])
  useEffect(() => {
    if (userInfo.role.key == "super_admin") {
      countPayments({
        count: true,
      })
    } else {
      countPayments({
        count: true,
        filter: [
          [
            {
              field: "ownerId",
              value: ownerInfo?.parentOwnerId,
              operator: "=",
            },
          ],
          [
            {
              field: "leaseId",
              operator: "notNull",
            },
          ],
        ],
      })
    }
    countPendingPayments({
      count: true,
      filter: generateFilterOption(PaymentStatus.Pending),
    })
    countRejectedPayments({
      count: true,
      filter: generateFilterOption(PaymentStatus.Rejected),
    })
    countSuccessPayments({
      count: true,
      filter: generateFilterOption(PaymentStatus.Success),
    })
  }, [])

  useEffect(() => {
    if (params?.id !== undefined) {
      setViewMode("detail")
    } else {
      setViewMode("list")
    }
    if (location.pathname === "/tenant/archived") {
      setViewMode("detail")
    }
  }, [setViewMode, params?.id, location])

  const data: Payment[] | undefined = payments.data?.data

  const config: EntityConfig<Payment> = {
    primaryColumn: {
      key: "name",
      name: t("lease", { count: 1 }),
      render(value) {
        return value?.lease?.name
      },
    },
    detailUrl: "detail",
    rootUrl: "/tenant",
    identity: "id",
    name: "name",
    showDetail: false,
    visibleColumn: [
      {
        key: "amount",
        name: t("amount"),
        render: (payment) => {
          return `${formatCurrency(payment?.amount as number)} ETB`
        },
      },
      {
        key: "paymentFrequency",
        name: t("paymentFrequency"),
        render: (payment) => {
          return payment?.paymentFrequency
        },
      },
      {
        key: "tenant",
        name: t("tenant", { count: 1 }),
        render(value) {
          return value?.tenant?.name
        },
      },
      {
        key: "owner",
        name: t("owner", { count: 1 }),
        render(value) {
          return value?.owner?.name
        },
      },
      {
        key: "lease",
        name: t("lease", { count: 1 }),
        render(value) {
          return value?.lease?.name
        },
      },
      {
        key: "fsNumber",
        name: t("fsNumber"),
        render(value) {
          return value?.fsNumber
        },
      },
      {
        key: "paymentProof",
        name: t("paymentProof"),
        render(value) {
          return (
            <>
              {value?.minioFile && (
                <span
                  // target="_blank"
                  // rel="noopener noreferrer"
                  // href={tenant?.data?.minoioIdentification}
                  // onClick={() => onViewFile(value?.file)}
                  onClick={() => {
                    setSelectedFile([value?.minioFile as string])
                    fileOpen()
                  }}
                  className=" cursor-pointer  text-white inline-block bg-primary  text-sm px-3 py-1 rounded-full"
                >
                  {t("view")}
                </span>
              )}
            </>
          )
        },
      },
      {
        key: "receipt",
        name: t("receipt"),
        render(value) {
          return (
            <>
              {value?.minioReceipt && (
                <span
                  // target="_blank"
                  // rel="noopener noreferrer"
                  // href={tenant?.data?.minoioIdentification}
                  // onClick={() => onViewFile(value?.receipt)}
                  onClick={() => {
                    setSelectedFile([value?.minioReceipt as string])
                    fileOpen()
                  }}
                  className=" cursor-pointer  text-white inline-block bg-primary  text-sm px-3 py-1 rounded-full"
                >
                  {t("view")}
                </span>
              )}
            </>
          )
        },
      },
      { key: "paymentMethod", name: t("paymentMethod") },
      { key: "paymentStatus", name: t("paymentStatus") },
      {
        key: "paymentDate",
        name: t("paymentDate"),
        render(value) {
          return dateFormat(value?.paymentDate, {
            day: true,
            month: true,
            weekday: true,
            year: true,
          })
        },
      },

      {
        key: "action",
        name: t("action"),
        render(payment) {
          return (
            <div className="flex gap-2">
              <Button
                variant="filled"
                loading={
                  updateResponse.isLoading &&
                  updateResponse?.originalArgs?.id === payment?.id
                }
                className={`bg-warning text-white flex space-x-2 items-center`}
                type="button"
                size={"xs"}
                onClick={() => {
                  setSelectedFsNumber(payment?.fsNumber as string)
                  setSelectedPayment(payment?.id as string)
                  openFs()
                }}
              >
                <IconEdit size={20} strokeWidth={1.5} />
              </Button>
              {payment?.paymentStatus == PaymentStatus.Pending && (
                <div className="flex gap-2">
                  <Confirmation
                    type={"notify"}
                    message={t("approvePaymentMessage")}
                    onYes={() => {
                      // deleteRentIncrease(payment?.id as string)
                      const formData = new FormData()

                      formData.append("id", payment?.id as string)
                      formData.append("status", PaymentStatus.Success)
                      receipt && formData.append("receipt", receipt)

                      changeStatus({
                        id: payment?.id as string,
                        data: formData,
                      })
                      // close()
                    }}
                    acceptsFile={
                      userInfo?.role?.key == "owner" ||
                      userInfo?.role?.key == "super_admin"
                    }
                    fileLabel={t("receipt")}
                    onAcceptingFile={(file: File) => {
                      setReceipt(file)
                    }}
                    header={t("approvePaymentTitle")}
                  >
                    <Button
                      variant="filled"
                      loading={
                        changeStatusResponse.isLoading &&
                        changeStatusResponse?.originalArgs?.id === payment?.id
                      }
                      className={`bg-green-500 text-white flex space-x-2 items-center`}
                      type="button"
                      classNames={{ label: "flex space-x-1" }}
                      size={"xs"}
                    >
                      <IconCheck size={20} strokeWidth={1.5} />
                    </Button>
                  </Confirmation>

                  <Confirmation
                    type={"danger"}
                    message={t("paymentRejectionMessage")}
                    onYes={() => {
                      const formData = new FormData()

                      formData.append("id", payment?.id as string)
                      formData.append("status", PaymentStatus.Rejected)
                      // deleteRentIncrease(payment?.id as string)
                      changeStatus({
                        id: payment?.id as string,
                        data: formData,
                      })
                      // close()
                    }}
                    header={t("paymentRejectionTitle")}
                  >
                    <Button
                      variant="filled"
                      loading={
                        changeStatusResponse.isLoading &&
                        changeStatusResponse?.originalArgs?.id === payment?.id
                      }
                      className={`bg-danger text-white flex space-x-2 items-center`}
                      type="button"
                      classNames={{ label: "flex space-x-1" }}
                      size={"xs"}
                    >
                      <IconX size={20} strokeWidth={1.5} />
                    </Button>
                  </Confirmation>
                </div>
              )}
            </div>
          )
        },
      },
    ],
  }

  const _parseConfig = () => {
    if (userInfo?.role?.key === "tenant") {
      return {
        ...config,
        visibleColumn: config.visibleColumn.filter(
          (column) => column.key !== "action",
        ),
      }
    } else {
      return config
    }
  }

  return (
    <main className="max-w-7xl mx-auto py-6 px-4 lg:px-8 mt-16">
      <LoadingOverlay visible={payments.isFetching} />

      <motion.div
        className="hidden md:grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8 relative z-10"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
      >
        <StatCard
          name={t("totalPayments")}
          icon={Package}
          value={totalPayments?.data?.count}
          color="#6366F1"
        />
        <StatCard
          name={t("pendingPayments")}
          icon={PauseCircle}
          value={pendingPayments?.data?.count}
          color="#8B5CF6"
        />
        <StatCard
          name={t("successPayments")}
          icon={TrendingUp}
          value={successPayments?.data?.count}
          color="#90EE90"
        />
        <StatCard
          name={t("rejectedPayments")}
          icon={AlertTriangle}
          value={rejectedPayments?.data?.count}
          color="#EC4899"
        />

        {userInfo?.role?.key == "owner" && ownerInfo?.isParent && (
          <>
            <TotalPaymentStatCard icon={DollarSign} color="#90EE90" />
            <TotalCollectedPaymentStatCard icon={DollarSign} color="#90EE90" />
          </>
        )}

        {/* <StatCard name='Total Revenue' icon={DollarSign} value='12.5%' color='#10B981' /> */}
      </motion.div>
      <EntityList
        viewMode={viewMode}
        title={t("payment", { count: 2 })}
        showExport={true}
        // header={
        //   <>
        //     <div className="h-14 dark:border-gray-500 dark:bg-gray-500 flex items-center justify-between p-2 border-0 border-b">
        //       <div className="h-full flex items-center space-x-4 font-semibold dark:text-white">
        //         {t("payment", { count: 2 })}
        //       </div>
        //       {
        //         <div className="h-full  space-x-4 flex items-center">
        //           {viewMode == "list" && (
        //             <>
        //               <Checkbox
        //                 size="sm"
        //                 onChange={(e) =>
        //                   setCollection({
        //                     ...collection,
        //                     withArchived: e.currentTarget.checked
        //                       ? true
        //                       : false,
        //                   })
        //                 }
        //                 label={
        //                   <span className="text-xs text-gray-500 dark:text-gray-400">
        //                     {t("showArchived")}
        //                   </span>
        //                 }
        //               />

        //               <Button
        //                 classNames={{ label: "flex space-x-2 text-white" }}
        //                 className="bg-primary dark:bg-dark_primary"
        //                 size="xs"
        //                 type="button"
        //                 onClick={(e) => openExport()}
        //                 variant="filled"
        //               >
        //                 <span className="text-white">
        //                   <IconFileExport />
        //                 </span>
        //                 <span>{t("export")}</span>
        //               </Button>
        //               <Button
        //                 classNames={{ label: "flex space-x-2 text-white" }}
        //                 className="bg-primary dark:bg-dark_primary"
        //                 size="xs"
        //                 type="button"
        //                 onClick={(e) => openImport()}
        //                 variant="filled"
        //               >
        //                 <span className="text-white">
        //                   <IconFileImport />
        //                 </span>
        //                 <span>{t("import")}</span>
        //               </Button>
        //             </>
        //           )}
        //           <Link to={`/tenant/new`} state={{ id: location?.state?.id }}>
        //             <Button
        //               classNames={{ label: "flex space-x-2 text-white" }}
        //               className="bg-primary dark:bg-dark_primary"
        //               size="xs"
        //               variant="filled"
        //             >
        //               <span className="text-white">
        //                 <svg
        //                   xmlns="http://www.w3.org/2000/svg"
        //                   className="fill-current h-4 text-white"
        //                   viewBox="0 0 24 24"
        //                 >
        //                   <path
        //                     fillRule="evenodd"
        //                     d="M11 2L11 11L2 11L2 13L11 13L11 22L13 22L13 13L22 13L22 11L13 11L13 2Z"
        //                   />
        //                 </svg>
        //               </span>
        //               <span>{t("new")}</span>
        //             </Button>
        //           </Link>
        //         </div>
        //       }
        //     </div>
        //   </>
        // }
        showNewButton={false}
        total={payments?.data?.count}
        itemsLoading={payments.isFetching}
        config={_parseConfig()}
        items={data}
        initialPage={1}
        defaultPageSize={10}
        pageSize={[10, 15, 20]}
        collectionQuery={collection}
        onArchivedChecked={(e) =>
          setCollection({
            ...collection,
            withArchived: e,
          })
        }
        onPaginationChange={(skip: number, top: number) => {
          if (collection.skip !== skip || collection.top !== top) {
            setCollection({ ...collection, skip: skip, top: top })
          }
        }}
        onSearch={(data: any) => {
          setCollection({
            ...collection,
            search: data,
            searchFrom: ["tenant.name", "tenant.phoneNumber", "fsNumber"],
          })
        }}
        onFilterChange={(data: any) => {
          if (collection?.filter || data.length > 0) {
            setCollection({ ...collection, filter: data })
          }
        }}
        onOrder={(data: Order) =>
          setCollection({ ...collection, orderBy: [data] })
        }
        onExport={openExport}
      />

      <ExportPaymentModalComponent
        open={openExport}
        opened={openedExport}
        close={closeExport}
        filename={"Payments.xlsx"}
        filteringVariables={[
          [
            {
              field: "paymentStatus",
              value: PaymentStatus.Pending,
              name: PaymentStatus.Pending,
              operator: "=",
            },
            {
              field: "paymentStatus",
              value: PaymentStatus.Success,
              name: PaymentStatus.Success,
              operator: "=",
            },
            {
              field: "paymentStatus",
              value: PaymentStatus.Expired,
              name: PaymentStatus.Expired,
              operator: "=",
            },
            {
              field: "paymentStatus",
              value: PaymentStatus.Failed,
              name: PaymentStatus.Failed,
              operator: "=",
            },
            {
              field: "paymentStatus",
              value: PaymentStatus.Rejected,
              name: PaymentStatus.Rejected,
              operator: "=",
            },
          ],
        ]}
        exportFunction={exportTenants}
      />

      <FileViewerModal
        open={fileOpen}
        close={fileClose}
        files={selectedFile}
        opened={fileOpened}
        fileType={t("payment", { count: 1 })}
        fileNames={[]}
        remove={() => {}}
        removeLoading={false}
      />

      <Modal
        opened={openedFs}
        onClose={closeFs}
        className="my-modal-content"
        withCloseButton={true}
        title={"Update FS Number"}
        padding={20}
        size={"sm"}
      >
        <div className="w-full">
          <div className="flex mb-4 w-full">
            <TextInput
              className="w-full text-sm"
              type="text"
              placeholder="Enter Fs Number"
              value={selectedFsNumber as string}
              onChange={(e) => setSelectedFsNumber(e.target.value)}
              // maw={400}
            />
          </div>

          <div className="mt-4  w-full flex justify-end">
            <Button
              loading={updateResponse.isLoading}
              onClick={() =>
                updateFsNumber({
                  id: selectedPayment as string,
                  fsNumber: selectedFsNumber as string,
                })
                  .unwrap()
                  .then((res) => {
                    closeFs()
                  })
              }
              size="xs"
              leftIcon={<IconEditCircle />}
              className="flex bg-primary text-white"
            >
              Update
            </Button>
          </div>
        </div>
      </Modal>
    </main>
  )
}
